(function () {
  if (customElements.get('asif-product-reviews')) return;

  function stars(rating) {
    return '★★★★★'.slice(0, rating) + '☆☆☆☆☆'.slice(rating);
  }

  function dateLabel(value) {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.valueOf())
      ? ''
      : new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(date);
  }

  class AsifProductReviews extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.apiBase = (this.dataset.apiBase || '').replace(/\/$/, '');
      this.productHandle = this.dataset.productHandle || '';
      this.includeShopReviews = this.dataset.includeShopReviews === '1';
      this.perPage = Number(this.dataset.perPage || 6);
      this.visibleLimit = this.perPage;
      this.reviews = [];
      this.dialog = this.querySelector('[data-review-dialog]');
      this.form = this.querySelector('[data-review-form]');
      this.bindControls();
      this.captureInvitation();
      this.load();
    }

    bindControls() {
      this.querySelector('[data-review-filter]')?.addEventListener('change', () => {
        this.visibleLimit = this.perPage;
        this.renderReviews();
      });
      this.querySelector('[data-review-sort]')?.addEventListener('change', () => {
        this.visibleLimit = this.perPage;
        this.renderReviews();
      });
      this.querySelectorAll('[data-review-distribution-rating]').forEach((button) => {
        button.addEventListener('click', () => {
          const rating = button.dataset.reviewDistributionRating;
          const filter = this.querySelector('[data-review-filter]');
          if (!rating || !filter) return;
          filter.value = filter.value === rating ? 'all' : rating;
          this.visibleLimit = this.perPage;
          this.renderReviews();
        });
      });
      this.querySelector('[data-review-more]')?.addEventListener('click', () => {
        this.visibleLimit += this.perPage;
        this.renderReviews();
      });
      this.querySelectorAll('[data-review-open]').forEach((button) => {
        button.addEventListener('click', () => this.openDialog());
      });
      this.querySelector('[data-review-close]')?.addEventListener('click', () =>
        this.dialog?.close(),
      );
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.form?.addEventListener('submit', (event) => this.submit(event));
    }

    captureInvitation() {
      const token = new URLSearchParams(window.location.search).get('review_token');
      if (!token) return;
      const input = this.querySelector('[data-review-token]');
      if (input) input.value = token;
      window.setTimeout(() => this.openDialog(), 200);
    }

    openDialog() {
      if (!this.dialog) return;
      this.dialog.showModal();
      window.setTimeout(() => this.form?.querySelector('input[name="rating"]')?.focus(), 50);
    }

    async load() {
      const status = this.querySelector('[data-review-status]');
      try {
        if (!this.apiBase || !this.productHandle) throw new Error('configuration_missing');
        const query = new URLSearchParams({ widget: 'v4' });
        if (this.includeShopReviews) query.set('include_shop', '1');
        const response = await fetch(
          `${this.apiBase}/v1/products/${encodeURIComponent(this.productHandle)}/reviews?${query}`,
          { headers: { accept: 'application/json' } },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        this.reviews = Array.isArray(data.reviews) ? data.reviews : [];
        const displayed = data.displayAggregate || data.aggregate;
        this.querySelector('[data-review-average]').textContent = displayed.average
          ? Number(displayed.average).toLocaleString('de-DE', { maximumFractionDigits: 1 })
          : '-';
        this.querySelector('[data-review-count]').textContent = `${displayed.count} ${
          displayed.count === 1 ? 'Bewertung' : 'Bewertungen'
        }`;
        const title = this.querySelector('[data-review-title]');
        if (title) {
          title.textContent = this.includeShopReviews
            ? 'Bewertungen aus unserem Shop.'
            : 'Bewertungen zu diesem Produkt.';
        }
        this.renderDistribution();
        const disclosureCount = this.querySelector('[data-review-disclosure-count]');
        if (disclosureCount) {
          disclosureCount.textContent = `${this.reviews.length} ${
            this.reviews.length === 1 ? 'Bewertung' : 'Bewertungen'
          }`;
        }
        this.querySelector('[data-review-content]').hidden = false;
        status.hidden = true;
        this.renderReviews();
      } catch (error) {
        status.hidden = false;
        status.textContent =
          'Die Bewertungen konnten gerade nicht geladen werden. Bitte versuche es später noch einmal.';
      }
    }

    selectedReviews() {
      const filter = this.querySelector('[data-review-filter]')?.value || 'all';
      const sort = this.querySelector('[data-review-sort]')?.value || 'recent';
      const selected = this.reviews.filter((review) => {
        if (/^[1-5]$/.test(filter)) return review.rating === Number(filter);
        return true;
      });
      selected.sort((a, b) => {
        if (sort === 'helpful') return b.helpfulCount - a.helpfulCount || b.id - a.id;
        if (sort === 'highest') return b.rating - a.rating || b.id - a.id;
        if (sort === 'lowest') return a.rating - b.rating || b.id - a.id;
        return (
          String(b.publishedAt || '').localeCompare(String(a.publishedAt || '')) || b.id - a.id
        );
      });
      return selected;
    }

    renderDistribution() {
      const total = this.reviews.length;
      this.querySelectorAll('[data-review-distribution-rating]').forEach((button) => {
        const rating = Number(button.dataset.reviewDistributionRating || 0);
        const count = this.reviews.filter((review) => review.rating === rating).length;
        const countNode = button.querySelector('[data-review-distribution-count]');
        const bar = button.querySelector('[data-review-distribution-bar]');
        if (countNode) countNode.textContent = String(count);
        if (bar) bar.style.width = `${total ? (count / total) * 100 : 0}%`;
        button.hidden = count === 0;
        button.setAttribute(
          'aria-label',
          `${count} ${count === 1 ? 'Bewertung' : 'Bewertungen'} mit ${rating} Sternen anzeigen`,
        );
      });
    }

    renderReviews() {
      const list = this.querySelector('[data-review-list]');
      const more = this.querySelector('[data-review-more]');
      const count = this.querySelector('[data-review-result-count]');
      if (!list || !more || !count) return;
      const selected = this.selectedReviews();
      const activeRating = this.querySelector('[data-review-filter]')?.value || 'all';
      this.querySelectorAll('[data-review-distribution-rating]').forEach((button) => {
        button.setAttribute(
          'aria-pressed',
          String(button.dataset.reviewDistributionRating === activeRating),
        );
      });
      list.replaceChildren(
        ...selected.slice(0, this.visibleLimit).map((review) => this.card(review)),
      );
      count.textContent = `${selected.length} ${selected.length === 1 ? 'Erfahrung' : 'Erfahrungen'}`;
      more.hidden = selected.length <= this.visibleLimit;
      if (selected.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'asif-reviews__empty';
        empty.textContent = 'Für diese Auswahl gibt es noch keine Bewertung.';
        list.append(empty);
      }
    }

    card(review) {
      const article = document.createElement('article');
      article.className = 'asif-review-card';

      const meta = document.createElement('div');
      meta.className = 'asif-review-card__meta';
      const rating = document.createElement('span');
      rating.className = 'asif-review-card__stars';
      rating.setAttribute('aria-label', `${review.rating} von 5 Sternen`);
      rating.textContent = stars(review.rating);
      meta.append(rating);
      if (review.title) {
        const title = document.createElement('h3');
        title.textContent = review.title;
        article.append(meta, title);
      } else {
        article.append(meta);
      }
      const body = document.createElement('p');
      body.className = 'asif-review-card__body';
      body.textContent = review.body;
      article.append(body);

      const footer = document.createElement('footer');
      const byline = document.createElement('p');
      const verification =
        review.verification === 'verified_buyer'
          ? ' / Verifizierter Kauf'
          : review.verification === 'verified_reviewer'
            ? ' / Identität geprüft'
            : '';
      byline.textContent = `${review.authorName}${verification}${dateLabel(review.publishedAt) ? ` / ${dateLabel(review.publishedAt)}` : ''}`;
      const helpful = document.createElement('button');
      helpful.type = 'button';
      helpful.className = 'asif-review-card__helpful';
      helpful.textContent = `Hilfreich ${review.helpfulCount ? `(${review.helpfulCount})` : ''}`;
      helpful.addEventListener('click', () => this.markHelpful(review, helpful));
      footer.append(byline, helpful);
      article.append(footer);
      return article;
    }

    async markHelpful(review, button) {
      if (button.disabled) return;
      button.disabled = true;
      try {
        const response = await fetch(`${this.apiBase}/v1/reviews/${review.id}/helpful`, {
          method: 'POST',
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        if (result.counted) review.helpfulCount += 1;
        button.textContent = result.counted
          ? `Hilfreich (${review.helpfulCount})`
          : 'Bereits gezählt';
      } catch {
        button.disabled = false;
        button.textContent = 'Noch einmal versuchen';
      }
    }

    async submit(event) {
      event.preventDefault();
      if (this.submitting || !this.form) return;
      const formData = new FormData(this.form);
      if (String(formData.get('website') || '').trim()) {
        this.showFormStatus('Vielen Dank! Deine Bewertung ist eingegangen.', true);
        return;
      }
      const turnstileToken = String(formData.get('cf-turnstile-response') || '');
      if (!turnstileToken) {
        this.showFormStatus('Bitte bestätige kurz die Sicherheitsprüfung.', false);
        return;
      }
      const submit = this.form.querySelector('[type="submit"]');
      this.submitting = true;
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      try {
        const payload = {
          productHandle: this.productHandle,
          authorName: String(formData.get('authorName') || ''),
          rating: Number(formData.get('rating')),
          body: String(formData.get('body') || ''),
          consent: formData.get('consent') === 'on',
          turnstileToken,
        };
        const optional = {
          authorEmail: String(formData.get('authorEmail') || '').trim(),
          title: String(formData.get('title') || '').trim(),
          invitationToken: String(formData.get('invitationToken') || '').trim(),
        };
        Object.entries(optional).forEach(([key, value]) => {
          if (value) payload[key] = value;
        });
        if (formData.get('recommend') === 'on') payload.recommend = true;

        const response = await fetch(`${this.apiBase}/v1/reviews`, {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.error || `HTTP ${response.status}`);
        }
        this.showFormStatus(
          'Danke. Deine Bewertung ist sicher eingegangen und wartet jetzt auf die redaktionelle Prüfung.',
          true,
        );
        const invitationToken = optional.invitationToken;
        this.form.reset();
        this.form.querySelector('[data-review-token]').value = invitationToken;
        window.turnstile?.reset();
      } catch (error) {
        const message =
          error.message === 'invalid_or_used_invitation'
            ? 'Dieser persönliche Bewertungslink wurde bereits verwendet oder ist abgelaufen.'
            : 'Das hat gerade nicht funktioniert. Bitte versuche es noch einmal oder schreib uns an hello@as-if-records.com.';
        this.showFormStatus(message, false);
        window.turnstile?.reset();
      } finally {
        this.submitting = false;
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
      }
    }

    showFormStatus(message, success) {
      const status = this.querySelector('[data-review-form-status]');
      status.hidden = false;
      status.dataset.kind = success ? 'success' : 'error';
      status.textContent = message;
    }
  }

  customElements.define('asif-product-reviews', AsifProductReviews);
})();
